import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Grid,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Avatar,
  useMediaQuery,
  useTheme,
  Stack,
  Checkbox,
    TextField,
  ButtonGroup,
  InputAdornment,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  VpnKey as VpnKeyIcon,
  WhatsApp as WhatsAppIcon,
  Comment as RemarkIcon,
} from '@mui/icons-material';
import { Booking } from '../types/bookingTypes';
import { bookingService } from '../services/bookingService';
import { generateAccessPass, GenerateAccessPassRequest } from '../services/serverService';
import { compareBookingsByAppointmentTime } from '../utils/bookingTime';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { collection, query, where, getDocs, orderBy, limit, addDoc, updateDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebase';

interface BookingDashboardStats {
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  completedBookings: number;
}

const ModernBookingDashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [selectedBookings, setSelectedBookings] = useState<string[]>([]);
  const [stats, setStats] = useState<BookingDashboardStats>({
    totalBookings: 0,
    pendingBookings: 0,
    confirmedBookings: 0,
    completedBookings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('active'); // Default to exclude completed
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [remarkDialogOpen, setRemarkDialogOpen] = useState(false);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedDateBookings, setSelectedDateBookings] = useState<Booking[]>([]);
  const [editedStatus, setEditedStatus] = useState<Booking['status']>('pending');
  const [deleting, setDeleting] = useState(false);
  const [remarkText, setRemarkText] = useState('');
  const [savingRemark, setSavingRemark] = useState(false);
  
  // Calendar navigation state
  const today = new Date();
  const [calendarMonth, setCalendarMonth] = useState(today.getMonth());
  const [calendarYear, setCalendarYear] = useState(today.getFullYear());
  
  // Access Pass generation state
  const [accessPassDialogOpen, setAccessPassDialogOpen] = useState(false);
  const [accessPassForm, setAccessPassForm] = useState({
    type: 'Store Visit',
    customerName: '',
    contactNumber: '',
    validDate: new Date().toISOString().split('T')[0],
    pickupTime: '11:00',
    returnTime: '20:00'
  });
  const [accessPassFormErrors, setAccessPassFormErrors] = useState<Record<string, string>>({});
  const [accessPassSubmitting, setAccessPassSubmitting] = useState(false);
  const [accessPassSuccess, setAccessPassSuccess] = useState(false);
  const [generatedAccessUrl, setGeneratedAccessUrl] = useState<string>('');
  
  // Global polling state
  const [globalPollingActive, setGlobalPollingActive] = useState(false);
  const [pendingTrackers, setPendingTrackers] = useState<any[]>([]);
  
  // Snackbar
    const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  useEffect(() => {
    loadBookingData();
    
    // Set up real-time listener
    const unsubscribe = bookingService.onBookingsChange((updatedBookings) => {
      setBookings(updatedBookings);
      setFilteredBookings(updatedBookings);
      calculateStats(updatedBookings);
    });

    // Start global access pass monitoring system
    startGlobalAccessPassMonitoring();

    return () => {
      unsubscribe();
      stopGlobalAccessPassMonitoring();
    };
  }, []);

  useEffect(() => {
    filterBookings();
  }, [bookings, searchQuery, statusFilter]);

  const loadBookingData = async () => {
    try {
      setRefreshing(true);
      const existingBookings = await bookingService.getBookings();
      if (existingBookings.length === 0) {
        await bookingService.addMockBookings();
      }

      const bookingsData = await bookingService.getBookings();
      setBookings(bookingsData);
      setFilteredBookings(bookingsData);
      calculateStats(bookingsData);
    } catch (error) {
      console.error('Error loading booking data:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error loading booking data', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const filterBookings = () => {
    let filtered = [...bookings];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(booking => 
        booking.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.phone?.includes(searchQuery)
      );
    }

    // Status filter
    if (statusFilter === 'active') {
      // Show all except completed bookings
      filtered = filtered.filter(booking => booking.status !== 'completed');
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    setFilteredBookings(filtered);
    setCurrentPage(1);
  };

  const calculateStats = (bookingsData: Booking[]) => {
    const totalBookings = bookingsData.length;
    const pendingBookings = bookingsData.filter(b => b.status === 'pending').length;
    const confirmedBookings = bookingsData.filter(b => b.status === 'confirmed').length;
    const completedBookings = bookingsData.filter(b => b.status === 'completed').length;

    setStats({
      totalBookings,
      pendingBookings,
      confirmedBookings,
      completedBookings,
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const pageBookings = getCurrentPageBookings();
      setSelectedBookings(pageBookings.map(b => b.id));
    } else {
      setSelectedBookings([]);
    }
  };

  const handleSelectBooking = (bookingId: string, checked: boolean) => {
    if (checked) {
      setSelectedBookings(prev => [...prev, bookingId]);
    } else {
      setSelectedBookings(prev => prev.filter(id => id !== bookingId));
    }
  };

  const getCurrentPageBookings = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredBookings.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  const handleViewBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setViewDialogOpen(true);
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setEditedStatus(booking.status);
    setEditDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedBooking) return;
    
    try {
      await bookingService.updateBookingStatus(selectedBooking.id, editedStatus);
      setSnackbar({ 
        open: true, 
        message: 'Booking status updated successfully!', 
        severity: 'success' 
      });
      setEditDialogOpen(false);
      loadBookingData();
    } catch (error) {
      console.error('Error updating booking:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error updating booking status', 
        severity: 'error' 
      });
    }
  };

  const handleDeleteBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBooking) return;
    
    try {
      setDeleting(true);
      await bookingService.deleteBooking(selectedBooking.id);
      setSnackbar({ 
        open: true, 
        message: 'Booking deleted successfully!', 
        severity: 'success' 
      });
      setDeleteDialogOpen(false);
      setSelectedBooking(null);
      // No need to call loadBookingData() as real-time listener will update
    } catch (error) {
      console.error('Error deleting booking:', error);
      setSnackbar({ 
        open: true, 
        message: 'Error deleting booking', 
        severity: 'error' 
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenRemark = (booking: Booking) => {
    setSelectedBooking(booking);
    setRemarkText(booking.remark || '');
    setRemarkDialogOpen(true);
  };

  const handleSaveRemark = async () => {
    if (!selectedBooking) return;

    try {
      setSavingRemark(true);
      await bookingService.updateBookingRemark(selectedBooking.id, remarkText.trim());
      setSnackbar({
        open: true,
        message: remarkText.trim() ? 'Appointment remark saved.' : 'Appointment remark cleared.',
        severity: 'success'
      });
      setRemarkDialogOpen(false);
    } catch (error) {
      console.error('Error saving booking remark:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save appointment remark.',
        severity: 'error'
      });
    } finally {
      setSavingRemark(false);
    }
  };

  const getStatusBadgeStyle = (status: Booking['status']) => {
    const baseStyle = {
      display: 'inline-block',
      px: 1.5,
      py: 0.5,
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase' as const,
      textAlign: 'center' as const,
      whiteSpace: 'nowrap' as const,
      minWidth: 'fit-content',
      maxWidth: '100px', // Ensure it doesn't extend beyond column
    };

    switch (status) {
      case 'pending':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'confirmed':
        return { ...baseStyle, backgroundColor: '#DBEAFE', color: '#1E40AF' };
      case 'completed':
        return { ...baseStyle, backgroundColor: '#D1FAE5', color: '#065F46' };
      case 'cancelled':
        return { ...baseStyle, backgroundColor: '#FEE2E2', color: '#991B1B' };
      default:
        return { ...baseStyle, backgroundColor: '#F3F4F6', color: '#374151' };
    }
  };

  // Removed getServiceProgress function as progress bars are no longer needed

  const getInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  const getDayOfWeek = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      // Convert to Malaysia timezone (UTC+8)
      const malaysiaDate = new Date(date.toLocaleString("en-US", {timeZone: "Asia/Kuala_Lumpur"}));
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[malaysiaDate.getDay()];
    } catch {
      return 'N/A';
    }
  };

  const getBookingsByDate = () => {
    const bookingsByDate: { [key: string]: Booking[] } = {};
    bookings.forEach(booking => {
      if (booking.consultationDate) {
        const dateKey = booking.consultationDate;
        if (!bookingsByDate[dateKey]) {
          bookingsByDate[dateKey] = [];
        }
        bookingsByDate[dateKey].push(booking);
      }
    });

    Object.values(bookingsByDate).forEach(dayBookings => {
      dayBookings.sort(compareBookingsByAppointmentTime);
    });

    return bookingsByDate;
  };

  const getTotalAppointmentCount = () => {
    const bookingsByDate = getBookingsByDate();
    return Object.keys(bookingsByDate).length;
  };

  const handleCalendarClick = () => {
    setCalendarDialogOpen(true);
  };

  const handleCalendarMonthChange = (month: number, year: number) => {
    setCalendarMonth(month);
    setCalendarYear(year);
  };

  const handleAccessPassFormChange = (field: string, value: string) => {
    setAccessPassForm({
      ...accessPassForm,
      [field]: value
    });
    
    // Clear error for this field if any
    if (accessPassFormErrors[field]) {
      setAccessPassFormErrors({
        ...accessPassFormErrors,
        [field]: ''
      });
    }
  };

  const validateAccessPassForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!accessPassForm.type) {
      errors.type = 'Please select a type';
    }
    
    if (!accessPassForm.customerName) {
      errors.customerName = 'Name is required';
    }
    
    if (!accessPassForm.contactNumber) {
      errors.contactNumber = 'Contact number is required';
    }
    
    if (!accessPassForm.validDate) {
      errors.validDate = 'Valid date is required';
    }
    
    if (!accessPassForm.pickupTime) {
      errors.pickupTime = 'Visit time in is required';
    }
    
    if (!accessPassForm.returnTime) {
      errors.returnTime = 'Visit time out is required';
    }
    
    setAccessPassFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Function to create a tracking record for access pass generation
  const createAccessPassTracker = async (customerName: string, contactNumber: string, validDate: string, type: string, pickupTime: string, returnTime: string) => {
    try {
      const trackerData = {
        customerName,
        contactNumber,
        validDate,
        type,
        pickupTime,
        returnTime,
        status: 'pending', // pending, found, failed
        accessUrl: null,
        createdAt: new Date(),
        lastChecked: new Date(),
        checkCount: 0,
        isLinked: false
      };
      
      const docRef = await addDoc(collection(db, 'access-pass-trackers'), trackerData);
      console.log('Created access pass tracker:', docRef.id, trackerData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating access pass tracker:', error);
      return null;
    }
  };

  // Helper function to normalize phone number for matching
  const normalizePhoneNumber = (phone: string): string[] => {
    // Remove all non-digit characters
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Return possible variations
    const variations = [
      digitsOnly, // Original: "0129689238"
      `60${digitsOnly}`, // With Malaysia code: "600129689238"
    ];
    
    // If starts with 0, also try without 0 and with country code
    if (digitsOnly.startsWith('0')) {
      const withoutZero = digitsOnly.substring(1); // "129689238"
      variations.push(
        withoutZero, // "129689238"
        `60${withoutZero}`, // "60129689238" - THIS should match Firebase!
      );
    }
    
    // If starts with 60, also try without it
    if (digitsOnly.startsWith('60')) {
      const without60 = digitsOnly.substring(2);
      variations.push(`0${without60}`); // Convert back to local format
    }
    
    console.log(`🔍 Phone normalization for "${phone}":`, variations);
    return Array.from(new Set(variations)); // Remove duplicates
  };

  // Helper function to normalize date format
  const normalizeDateFormat = (dateStr: string): string[] => {
    // Input: "2025-07-14" (YYYY-MM-DD)
    // Output: ["2025-07-14", "14/07/2025"]
    
    const variations = [dateStr]; // Always include original
    
    if (dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      // Firebase uses DD/MM/YYYY format like "14/07/2025" with zero-padded month
      const firebaseFormat = `${parseInt(day)}/${month}/${year}`; // Keep zero-padded month
      variations.push(firebaseFormat);
    } else if (dateStr.includes('/')) {
      // Convert DD/MM/YYYY to YYYY-MM-DD
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const isoFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        variations.push(isoFormat);
      }
    }
    
    console.log(`📅 Date normalization for "${dateStr}":`, variations);
    return Array.from(new Set(variations)); // Remove duplicates
  };

  // Function to normalize customer name for case-insensitive matching
  const normalizeCustomerName = (name: string): string[] => {
    const variations = [
      name, // Original: "Elaine Low"
      name.toLowerCase(), // Lowercase: "elaine low" - THIS should match Firebase!
      name.toUpperCase(), // Uppercase: "ELAINE LOW"
      name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' '), // Title case
    ];
    
    console.log(`👤 Name normalization for "${name}":`, variations);
    return Array.from(new Set(variations)); // Remove duplicates
  };

  // Function to search for generated access pass using composite key
  const findGeneratedAccessPass = async (customerName: string, contactNumber: string, validDate: string, type: string) => {
    try {
      console.log('Searching for access pass with:', { customerName, contactNumber, validDate, type });
      
      // Get all possible variations
      const nameVariations = normalizeCustomerName(customerName);
      const phoneVariations = normalizePhoneNumber(contactNumber);
      const dateVariations = normalizeDateFormat(validDate);
      
      console.log('Name variations to try:', nameVariations);
      console.log('Phone variations to try:', phoneVariations);
      console.log('Date variations to try:', dateVariations);
      
      // Try each combination of name, phone and date variations
      for (const nameVar of nameVariations) {
        for (const phoneVar of phoneVariations) {
          for (const dateVar of dateVariations) {
            console.log(`Trying: name="${nameVar}", phone="${phoneVar}", date="${dateVar}"`);
            
            try {
              // Query Firebase for access passes matching our criteria
              const accessPassQuery = query(
                collection(db, 'Emhub Access Pass'),
                where('Visitor Name', '==', nameVar),
                where('Contact Number', '==', phoneVar),
                where('Date', '==', dateVar),
                where('Type', '==', type),
                orderBy('Collection Time', 'desc'),
                limit(1)
              );
              
              const querySnapshot = await getDocs(accessPassQuery);
              
              if (!querySnapshot.empty) {
                const accessPassDoc = querySnapshot.docs[0];
                const accessPassData = accessPassDoc.data();
                console.log('🎉 Found matching access pass:', accessPassData);
                return { id: accessPassDoc.id, ...accessPassData } as any;
              }
            } catch (queryError) {
              console.log(`Query failed for name="${nameVar}", phone="${phoneVar}", date="${dateVar}":`, queryError);
              // Continue with next variation
            }
          }
        }
      }
      
      console.log('No matching access pass found with any variation');
      return null;
    } catch (error) {
      console.error('Error searching for access pass:', error);
      return null;
    }
  };

  // Function to update tracker when access pass is found
  const linkAccessPassToTracker = async (trackerId: string, accessPassData: any) => {
    try {
      await updateDoc(doc(db, 'access-pass-trackers', trackerId), {
        status: 'found',
        accessUrl: accessPassData['Emhub Access'] || accessPassData['request new access'],
        isLinked: true,
        foundAt: new Date(),
        lastChecked: new Date(),
        accessPassId: accessPassData.id,
        emhubAccessUrl: accessPassData['Emhub Access'],
        requestNewAccessUrl: accessPassData['request new access']
      });
      console.log('Successfully linked access pass to tracker:', trackerId);
      return true;
    } catch (error) {
      console.error('Error linking access pass to tracker:', error);
      return false;
    }
  };

  // Function to update tracker check count
  const updateTrackerCheckCount = async (trackerId: string, checkCount: number) => {
    try {
      await updateDoc(doc(db, 'access-pass-trackers', trackerId), {
        lastChecked: new Date(),
        checkCount: checkCount
      });
    } catch (error) {
      console.error('Error updating tracker check count:', error);
    }
  };

  // Background polling function that runs every 30 seconds
  const startBackgroundPolling = (trackerId: string, customerName: string, contactNumber: string, validDate: string, type: string) => {
    let checkCount = 0;
    const maxChecks = 720; // 720 * 30 seconds = 6 hours max
    
    const pollInterval = setInterval(async () => {
      checkCount++;
      console.log(`Background polling attempt ${checkCount}/${maxChecks} for tracker ${trackerId}`);
      
      try {
        // Update check count in tracker
        await updateTrackerCheckCount(trackerId, checkCount);
        
        // Search for the access pass
        const accessPass = await findGeneratedAccessPass(customerName, contactNumber, validDate, type);
        
        if (accessPass && (accessPass['Emhub Access'] || accessPass['request new access'])) {
          console.log('🎉 Access pass found after', checkCount, 'attempts:', accessPass);
          
          // Link the access pass to tracker
          const linked = await linkAccessPassToTracker(trackerId, accessPass);
          
          if (linked) {
            // Show success notification
            setSnackbar({
              open: true,
              message: `Access pass URL ready for ${customerName}! Check the Access Passes section.`,
              severity: 'success'
            });
            
            // Store the URL for immediate display if dialog is still open
            setGeneratedAccessUrl(accessPass['Emhub Access'] || accessPass['request new access']);
          }
          
          // Stop polling
          clearInterval(pollInterval);
          console.log('Background polling stopped - access pass found and linked');
        } else if (checkCount >= maxChecks) {
          // Max attempts reached
          console.log('Max polling attempts reached for tracker:', trackerId);
          
          // Update tracker status to failed
          await updateDoc(doc(db, 'access-pass-trackers', trackerId), {
            status: 'failed',
            lastChecked: new Date(),
            checkCount: checkCount,
            failedReason: 'Max attempts reached'
          });
          
          // Show warning notification
          setSnackbar({
            open: true,
            message: `Access pass generation is taking longer than expected for ${customerName}. Will continue checking...`,
            severity: 'warning'
          });
          
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error('Error in background polling:', error);
        
        if (checkCount >= maxChecks) {
          clearInterval(pollInterval);
        }
      }
    }, 30000); // Poll every 30 seconds
    
    console.log('Started background polling for tracker:', trackerId, 'checking every 30 seconds');
    
    // Also do an immediate check after 30 seconds (first check)
    setTimeout(async () => {
      checkCount++;
      const accessPass = await findGeneratedAccessPass(customerName, contactNumber, validDate, type);
      if (accessPass && (accessPass['Emhub Access'] || accessPass['request new access'])) {
        console.log('🎉 Access pass found on first check:', accessPass);
        const linked = await linkAccessPassToTracker(trackerId, accessPass);
        if (linked) {
          setSnackbar({
            open: true,
            message: `Access pass URL ready for ${customerName}!`,
            severity: 'success'
          });
          setGeneratedAccessUrl(accessPass['Emhub Access'] || accessPass['request new access']);
        }
        clearInterval(pollInterval);
      }
    }, 30000);
    
    return pollInterval;
  };

  // Global monitoring system for all pending trackers
  let globalMonitoringInterval: NodeJS.Timeout | null = null;

  const startGlobalAccessPassMonitoring = () => {
    console.log('🌐 Starting global access pass monitoring system...');
    setGlobalPollingActive(true);
    
    // Check immediately on start
    checkAllPendingTrackers();
    
    // Then check every 30 seconds
    globalMonitoringInterval = setInterval(() => {
      checkAllPendingTrackers();
    }, 30000);
  };

  const stopGlobalAccessPassMonitoring = () => {
    console.log('🛑 Stopping global access pass monitoring system...');
    setGlobalPollingActive(false);
    
    if (globalMonitoringInterval) {
      clearInterval(globalMonitoringInterval);
      globalMonitoringInterval = null;
    }
  };

  const checkAllPendingTrackers = async () => {
    try {
      console.log('🔍 Checking all pending access pass trackers...');
      
      // Query for all pending (not linked) trackers
      const pendingTrackersQuery = query(
        collection(db, 'access-pass-trackers'),
        where('isLinked', '==', false),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
        limit(50) // Check max 50 pending at a time
      );
      
      const trackersSnapshot = await getDocs(pendingTrackersQuery);
      const trackers = trackersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      console.log(`Found ${trackers.length} pending trackers to check`);
      setPendingTrackers(trackers);
      
      // Check each tracker for matching access pass
      for (const tracker of trackers) {
        await checkSingleTracker(tracker);
      }
      
    } catch (error) {
      console.error('Error checking pending trackers:', error);
    }
  };

  const checkSingleTracker = async (tracker: any) => {
    try {
      console.log(`Checking tracker ${tracker.id} for:`, {
        name: tracker.customerName,
        phone: tracker.contactNumber,
        date: tracker.validDate,
        type: tracker.type
      });
      
      // Search for matching access pass
      const accessPass = await findGeneratedAccessPass(
        tracker.customerName,
        tracker.contactNumber,
        tracker.validDate,
        tracker.type
      );
      
      if (accessPass && (accessPass['Emhub Access'] || accessPass['request new access'])) {
        console.log('🎉 Found matching access pass for tracker:', tracker.id);
        
        // Link the access pass to tracker
        const linked = await linkAccessPassToTracker(tracker.id, accessPass);
        
        if (linked) {
          // Show notification to user
          setSnackbar({
            open: true,
            message: `✅ Access pass ready for ${tracker.customerName}! URL generated.`,
            severity: 'success'
          });
          
          console.log(`Successfully linked access pass for ${tracker.customerName}`);
        }
      } else {
        // Update check count
        const newCheckCount = (tracker.checkCount || 0) + 1;
        await updateTrackerCheckCount(tracker.id, newCheckCount);
        
        // If too many attempts, mark as failed
        if (newCheckCount >= 720) { // 6 hours worth of checks
          await updateDoc(doc(db, 'access-pass-trackers', tracker.id), {
            status: 'failed',
            lastChecked: new Date(),
            failedReason: 'Max attempts reached (6 hours)'
          });
          
          setSnackbar({
            open: true,
            message: `⚠️ Access pass generation timed out for ${tracker.customerName}`,
            severity: 'warning'
          });
        }
      }
    } catch (error) {
      console.error(`Error checking tracker ${tracker.id}:`, error);
    }
  };

  // Open a direct WhatsApp conversation without a prefilled message.
  const openWhatsApp = (booking: Booking) => {
    const whatsappUrl = getWhatsAppUrl(booking.phone, booking.phoneCountry);

    if (!whatsappUrl) {
      setSnackbar({
        open: true,
        message: 'This booking does not have a valid phone number.',
        severity: 'warning'
      });
      return;
    }

    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const submitAccessPassForm = async () => {
    if (!validateAccessPassForm()) {
      return;
    }
    
    try {
      setAccessPassSubmitting(true);
      
      // Create the request body for the server
      const requestBody: GenerateAccessPassRequest = {
        customerName: accessPassForm.customerName,
        contactNumber: accessPassForm.contactNumber,
        validDate: accessPassForm.validDate,
        pickupTime: accessPassForm.pickupTime,
        returnTime: accessPassForm.returnTime,
        type: accessPassForm.type,
        qrid: '1000000000000' // Default QRID for ad hoc passes
      };
      
      // Submit to server
      const response = await generateAccessPass(requestBody);
      
      if (response.success) {
        console.log('Access pass created successfully:', response);
        setAccessPassSuccess(true);
        
                setSnackbar({
          open: true,
          message: 'Access pass submitted! Background monitoring started...',
          severity: 'info'
        });
        
        // Create tracking record and start background polling
        const trackerId = await createAccessPassTracker(
          accessPassForm.customerName,
          accessPassForm.contactNumber,
          accessPassForm.validDate,
          accessPassForm.type,
          accessPassForm.pickupTime,
          accessPassForm.returnTime
        );
        
        if (trackerId) {
          setSnackbar({
            open: true,
            message: `✅ Access pass tracking started for ${accessPassForm.customerName}. Global monitoring will notify you when ready!`,
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Access pass submitted, but tracking setup failed. Please check Access Passes section manually.',
            severity: 'warning'
          });
        }
        
        // Reset form after success
        setAccessPassForm({
          type: 'Store Visit',
          customerName: '',
          contactNumber: '',
          validDate: new Date().toISOString().split('T')[0],
          pickupTime: '11:00',
          returnTime: '20:00'
        });
        
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          setAccessPassDialogOpen(false);
          setAccessPassSuccess(false);
        }, 1500);
      } else {
        console.error('Failed to create access pass:', response.error);
        setSnackbar({
          open: true,
          message: `Failed to create access pass: ${response.error || 'Unknown error'}`,
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error creating access pass:', err);
      setSnackbar({
        open: true,
        message: 'Failed to create access pass. Please try again.',
        severity: 'error'
      });
    } finally {
      setAccessPassSubmitting(false);
    }
  };

  // Calendar Grid Component
  const CalendarGrid: React.FC<{
    bookingsByDate: Record<string, Booking[]>;
    onDateClick: (date: string, dayBookings: Booking[]) => void;
    selectedDate: string | null;
    currentMonth: number;
    currentYear: number;
    onMonthChange: (month: number, year: number) => void;
  }> = React.memo(({ bookingsByDate, onDateClick, selectedDate, currentMonth, currentYear, onMonthChange }) => {
    
    // Get first day of month and number of days
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday
    
    // Convert to Monday-first (0 = Monday, 6 = Sunday)
    const mondayFirstDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    // Get days from previous month to fill the grid
    const daysFromPrevMonth = mondayFirstDay;
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const lastDayOfPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();
    
    // Generate calendar days
    const calendarDays = [];
    
    // Previous month days (grayed out)
    for (let i = daysFromPrevMonth - 1; i >= 0; i--) {
      const day = lastDayOfPrevMonth - i;
      calendarDays.push({
        day,
        isCurrentMonth: false,
        date: `${prevYear}-${String(prevMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      });
    }
    
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calendarDays.push({
        day,
        isCurrentMonth: true,
        date: dateString
      });
    }
    
    // Next month days to fill the grid (if needed)
    const remainingCells = 42 - calendarDays.length; // 6 rows × 7 days
    const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    
    for (let day = 1; day <= remainingCells; day++) {
      const dateString = `${nextYear}-${String(nextMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calendarDays.push({
        day,
        isCurrentMonth: false,
        date: dateString
      });
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Navigation functions
    const goToPreviousMonth = () => {
      console.log('Previous month clicked, current:', currentMonth, currentYear);
      if (currentMonth === 0) {
        onMonthChange(11, currentYear - 1);
        console.log('Going to December of previous year:', 11, currentYear - 1);
      } else {
        onMonthChange(currentMonth - 1, currentYear);
        console.log('Going to previous month:', currentMonth - 1, currentYear);
      }
    };

    const goToNextMonth = () => {
      console.log('Next month clicked, current:', currentMonth, currentYear);
      if (currentMonth === 11) {
        onMonthChange(0, currentYear + 1);
        console.log('Going to January of next year:', 0, currentYear + 1);
      } else {
        onMonthChange(currentMonth + 1, currentYear);
        console.log('Going to next month:', currentMonth + 1, currentYear);
      }
    };

    const goToToday = () => {
      const today = new Date();
      onMonthChange(today.getMonth(), today.getFullYear());
    };

    return (
      <Box sx={{ p: 3 }}>
        {/* Calendar Header with Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={goToPreviousMonth} sx={{ color: '#667085' }}>
            <ChevronLeftIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#101828' }}>
              {monthNames[currentMonth]} {currentYear}
            </Typography>
            <Button 
              size="small" 
              onClick={goToToday}
              sx={{ 
                color: '#7C3AED', 
                fontSize: '12px',
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Today
            </Button>
          </Box>
          
          <IconButton onClick={goToNextMonth} sx={{ color: '#667085' }}>
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* Calendar Container with fixed width */}
        <Box sx={{ 
          maxWidth: 600,
          margin: '0 auto'
        }}>
          {/* Days of week header */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            gap: 1,
            mb: 2
          }}>
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  minHeight: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  color: '#6B7280',
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* Calendar Grid */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(7, 1fr)', 
            gap: 1
          }}>
          {calendarDays.map((calendarDay, index) => {
            const appointmentCount = bookingsByDate[calendarDay.date]?.length || 0;
            const hasAppointments = appointmentCount > 0;
            const todayString = new Date().toISOString().split('T')[0];
            const isToday = calendarDay.date === todayString && calendarDay.isCurrentMonth;
            const isSelected = selectedDate === calendarDay.date;
            
            return (
              <Box
                key={index}
                onClick={() => {
                  if (hasAppointments && calendarDay.isCurrentMonth) {
                    onDateClick(calendarDay.date, bookingsByDate[calendarDay.date]);
                  }
                }}
                sx={{
                  position: 'relative',
                  p: 1,
                  minHeight: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '8px',
                  cursor: hasAppointments && calendarDay.isCurrentMonth ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  color: calendarDay.isCurrentMonth ? '#111827' : '#9CA3AF',
                  backgroundColor: isSelected ? '#EFF6FF' : 'transparent',
                  border: isToday ? '2px solid #2563EB' : '1px solid transparent',
                  '&:hover': hasAppointments && calendarDay.isCurrentMonth ? {
                    backgroundColor: '#F3F4F6',
                  } : {}
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: isToday ? 600 : 500,
                    color: isToday ? '#2563EB' : 'inherit'
                  }}
                >
                  {calendarDay.day}
                </Typography>
                
                {/* Appointment count badge */}
                {hasAppointments && calendarDay.isCurrentMonth && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: '#7C3AED',
                      color: 'white',
                      borderRadius: '50%',
                      minWidth: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      fontWeight: 600
                    }}
                  >
                    {appointmentCount}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        
          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: '#667085' }}>
            Click on a date with appointments to view details
          </Typography>
        </Box>
      </Box>
    );
  });

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading booking dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh',
      display: 'flex', 
      flexDirection: 'column',
      p: 2, // Add back some padding
      overflow: 'hidden', // Prevent page scrolling
      position: 'relative', // Change to relative positioning
      backgroundColor: 'background.default'
    }}>
      {/* Header - Fixed */}
      <Card sx={{ 
        mb: 2, 
        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
        border: '1px solid #EAECF0',
        borderRadius: '8px',
        flexShrink: 0
      }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#101828' }}>
                Booking Management
              </Typography>
              
              {/* Global monitoring status indicator */}
              {globalPollingActive && (
                <Chip
                  label={`🔄 Monitoring ${pendingTrackers.length} pending`}
                  size="small"
                  sx={{
                    backgroundColor: '#DBEAFE',
                    color: '#1E40AF',
                    fontSize: '0.75rem',
                    height: '24px',
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ color: '#667085' }}>
              Keep track of bookings and their status ratings.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              sx={{ 
                borderColor: '#D0D5DD',
                color: '#344054',
                '&:hover': { borderColor: '#98A2B3' }
              }}
            >
              Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<CalendarIcon />}
              onClick={handleCalendarClick}
              sx={{ 
                borderColor: '#D0D5DD',
                color: '#344054',
                position: 'relative',
                '&:hover': { borderColor: '#98A2B3' }
              }}
            >
              Calendar
              {getTotalAppointmentCount() > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: '#DC2626',
                    color: 'white',
                    borderRadius: '50%',
                    minWidth: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    border: '2px solid white',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  {getTotalAppointmentCount()}
                </Box>
              )}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ 
                backgroundColor: '#7C3AED',
                '&:hover': { backgroundColor: '#6D28D9' }
              }}
            >
              Add Booking
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* Filter Bar - Fixed */}
      <Card sx={{ 
        mb: 2,
        boxShadow: 'none',
        border: '1px solid #EAECF0',
        borderRadius: '12px',
        flexShrink: 0
      }}>
        <Box sx={{ 
          p: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 3,
          flexWrap: isMobile ? 'wrap' : 'nowrap'
        }}>
          <ButtonGroup 
            variant="outlined"
            sx={{
              '& .MuiButton-root': {
                px: 2.5,
                py: 1,
                minWidth: '80px'
              }
            }}
          >
            <Button 
              onClick={() => setStatusFilter('active')}
              sx={{ 
                backgroundColor: statusFilter === 'active' ? '#F9FAFB' : 'white',
                borderColor: '#D0D5DD',
                color: '#344054',
                fontWeight: statusFilter === 'active' ? 600 : 500
              }}
            >
              Active
            </Button>
            <Button 
              onClick={() => setStatusFilter('all')}
              sx={{ 
                backgroundColor: statusFilter === 'all' ? '#F9FAFB' : 'white',
                borderColor: '#D0D5DD',
                color: '#344054',
                fontWeight: statusFilter === 'all' ? 600 : 500
              }}
            >
              All
            </Button>
            <Button 
              onClick={() => setStatusFilter('pending')}
              sx={{ 
                backgroundColor: statusFilter === 'pending' ? '#F9FAFB' : 'white',
                borderColor: '#D0D5DD',
                color: '#344054',
                fontWeight: statusFilter === 'pending' ? 600 : 500
              }}
            >
              Pending
            </Button>
            <Button 
              onClick={() => setStatusFilter('confirmed')}
              sx={{ 
                backgroundColor: statusFilter === 'confirmed' ? '#F9FAFB' : 'white',
                borderColor: '#D0D5DD',
                color: '#344054',
                fontWeight: statusFilter === 'confirmed' ? 600 : 500
              }}
            >
              Confirmed
            </Button>
            <Button 
              onClick={() => setStatusFilter('completed')}
              sx={{ 
                backgroundColor: statusFilter === 'completed' ? '#F9FAFB' : 'white',
                borderColor: '#D0D5DD',
                color: '#344054',
                fontWeight: statusFilter === 'completed' ? 600 : 500
              }}
            >
              Completed
            </Button>
          </ButtonGroup>

          <Stack 
            direction="row" 
            spacing={2.5} 
            alignItems="center"
            sx={{ 
              flex: isMobile ? '1 1 100%' : '0 0 auto',
              mt: isMobile ? 2 : 0,
              justifyContent: isMobile ? 'flex-end' : 'flex-end'
            }}
          >
            <TextField
              size="small"
              placeholder="Search bookings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#667085', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                width: isMobile ? 250 : 350,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#D0D5DD',
                  },
                  '&:hover fieldset': {
                    borderColor: '#98A2B3',
                  },
                }
              }}
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadBookingData}
              disabled={refreshing}
              sx={{ 
                borderColor: '#D0D5DD',
                color: '#344054',
                px: 2.5,
                py: 1,
                minWidth: '100px',
                '&:hover': { borderColor: '#98A2B3' },
                '&:disabled': {
                  borderColor: '#E5E7EB',
                  color: '#9CA3AF'
                }
              }}
            >
              {isMobile ? 'Refresh' : 'Refresh'}
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* Data Table - Scrollable */}
      <Card sx={{ 
        boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.10)',
        border: '1px solid #EAECF0',
        borderRadius: '8px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Table Header - Fixed */}
        <Box sx={{ 
          borderBottom: '1px solid #EAECF0',
          backgroundColor: '#FCFCFD',
          p: 2,
          flexShrink: 0
        }}>
          <Grid container alignItems="center">
            <Grid item xs={isMobile ? 5 : 2.5}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  checked={selectedBookings.length === getCurrentPageBookings().length && getCurrentPageBookings().length > 0}
                  indeterminate={selectedBookings.length > 0 && selectedBookings.length < getCurrentPageBookings().length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  size="small"
                />
                <Typography variant="subtitle2" sx={{ ml: 1, color: '#667085', fontWeight: 500 }}>
                  Customer
                </Typography>
              </Box>
            </Grid>
            {!isMobile && (
              <Grid item xs={1.5}>
                <Typography variant="subtitle2" sx={{ color: '#667085', fontWeight: 500 }}>
                  Service
                </Typography>
              </Grid>
            )}
            <Grid item xs={isMobile ? 2 : 1.3}>
              <Typography variant="subtitle2" sx={{ color: '#667085', fontWeight: 500 }}>
                Status
              </Typography>
            </Grid>
            <Grid item xs={isMobile ? 2 : 1.3}>
              <Typography variant="subtitle2" sx={{ color: '#667085', fontWeight: 500 }}>
                Date
              </Typography>
            </Grid>
            {!isMobile && (
              <Grid item xs={1.2}>
                <Typography variant="subtitle2" sx={{ color: '#667085', fontWeight: 500 }}>
                  Day
                </Typography>
              </Grid>
            )}
            {!isMobile && (
              <Grid item xs={1.2}>
                <Typography variant="subtitle2" sx={{ color: '#667085', fontWeight: 500 }}>
                  Time
                </Typography>
              </Grid>
            )}
            {!isMobile && (
              <Grid item xs={1.5}>
                <Typography variant="subtitle2" sx={{ color: '#667085', fontWeight: 500 }}>
                  Tags
                </Typography>
              </Grid>
            )}
            <Grid item xs={isMobile ? 3 : 1.5}>
              <Typography variant="subtitle2" sx={{ color: '#667085', fontWeight: 500 }}>
                Actions
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Scrollable Body */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a8a8a8',
          }
        }}>
          {getCurrentPageBookings().map((booking, index) => (
            <Box 
              key={booking.id} 
              sx={{ 
                p: 2, 
                borderBottom: index < getCurrentPageBookings().length - 1 ? '1px solid #EAECF0' : 'none',
                '&:hover': { backgroundColor: '#F9FAFB' }
              }}
            >
              <Grid container alignItems="center">
                {/* Customer */}
                <Grid item xs={isMobile ? 5 : 2.5}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Checkbox
                      checked={selectedBookings.includes(booking.id)}
                      onChange={(e) => handleSelectBooking(booking.id, e.target.checked)}
                      size="small"
                    />
                    <Avatar 
                      sx={{ 
                        width: 28, 
                        height: 28, 
                        fontSize: '0.7rem',
                        backgroundColor: '#7C3AED',
                        color: 'white'
                      }}
                    >
                      {getInitials(booking.fullName || booking.name || '')}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 500, 
                          color: '#101828', 
                          fontSize: '0.8rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {isMobile ? (booking.fullName || booking.name)?.split(' ')[0] : (booking.fullName || booking.name)}
                      </Typography>
                      {!isMobile && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#667085',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'block'
                          }}
                        >
                          {booking.email}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Grid>

                {/* Service - Desktop Only */}
                {!isMobile && (
                  <Grid item xs={1.5}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500, 
                        color: '#101828',
                        fontSize: '0.8rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {booking.serviceType || booking.eventType}
                    </Typography>
                  </Grid>
                )}

                {/* Status */}
                <Grid item xs={isMobile ? 2 : 1.3}>
                  <Box sx={getStatusBadgeStyle(booking.status)}>
                    {isMobile ? booking.status.charAt(0).toUpperCase() : booking.status}
                  </Box>
                </Grid>

                {/* Date */}
                <Grid item xs={isMobile ? 2 : 1.3}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#667085', 
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {isMobile ? 
                      formatDate(booking.consultationDate).split(' ')[1] + ' ' + formatDate(booking.consultationDate).split(' ')[0] : 
                      formatDate(booking.consultationDate)
                    }
                  </Typography>
                </Grid>

                {/* Day - Desktop Only */}
                {!isMobile && (
                  <Grid item xs={1.2}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#374151', 
                        fontSize: '0.8rem', 
                        fontWeight: 600,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {getDayOfWeek(booking.consultationDate)}
                    </Typography>
                  </Grid>
                )}

                {/* Time - Desktop Only */}
                {!isMobile && (
                  <Grid item xs={1.2}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#667085', 
                        fontSize: '0.8rem', 
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {booking.consultationTime || 'N/A'}
                    </Typography>
                  </Grid>
                )}

                {/* Tags - Desktop Only */}
                {!isMobile && (
                  <Grid item xs={1.5}>
                    <Chip 
                      label={booking.serviceName || booking.serviceCategory || 'Standard'} 
                      size="small"
                      sx={{ 
                        backgroundColor: '#F0F9FF',
                        color: '#0369A1',
                        fontWeight: 500,
                        fontSize: '0.65rem',
                        height: '18px',
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }
                      }}
                    />
                  </Grid>
                )}

                {/* Actions */}
                <Grid item xs={isMobile ? 3 : 1.5}>
                  <Stack direction="row" spacing={0.2}>
                    <IconButton
                      size="small"
                      onClick={() => handleViewBooking(booking)}
                      aria-label="View booking details"
                      sx={{ 
                        color: '#667085',
                        '&:hover': { backgroundColor: '#F9FAFB', color: '#344054' },
                        padding: '4px'
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEditBooking(booking)}
                      aria-label="Update booking status"
                      sx={{ 
                        color: '#667085',
                        '&:hover': { backgroundColor: '#F9FAFB', color: '#344054' },
                        padding: '4px'
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteBooking(booking)}
                      aria-label="Delete booking"
                      sx={{ 
                        color: '#DC2626',
                        '&:hover': { backgroundColor: '#FEF2F2', color: '#B91C1C' },
                        padding: '4px'
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenRemark(booking)}
                      aria-label={booking.remark ? 'Edit appointment remark' : 'Add appointment remark'}
                      sx={{ 
                        color: booking.remark ? '#D97706' : '#667085',
                        '&:hover': { backgroundColor: '#FFFBEB', color: '#B45309' },
                        padding: '4px'
                      }}
                    >
                      <RemarkIcon fontSize="small" />
                    </IconButton>

                    {booking.phone && (
                      <IconButton
                        size="small"
                        onClick={() => openWhatsApp(booking)}
                        aria-label={`Open WhatsApp for ${booking.fullName || booking.name || 'customer'}`}
                        sx={{ 
                          color: '#25D366',
                          '&:hover': { backgroundColor: '#F0FDF4', color: '#16A34A' },
                          padding: '4px'
                        }}
                      >
                        <WhatsAppIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>

        {/* Pagination - Fixed */}
        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid #EAECF0',
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'white',
          flexShrink: 0
        }}>
          <Typography variant="body2" sx={{ color: '#344054', fontWeight: 500, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            {isMobile ? `${currentPage}/${totalPages}` : `Page ${currentPage} of ${totalPages} • Showing ${getCurrentPageBookings().length} of ${filteredBookings.length} bookings`}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              sx={{ 
                borderColor: '#D0D5DD',
                color: '#344054',
                '&:hover': { borderColor: '#98A2B3' },
                minWidth: isMobile ? '70px' : 'auto'
              }}
            >
              {isMobile ? 'Prev' : 'Previous'}
            </Button>
            <Button
              variant="outlined"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              sx={{ 
                borderColor: '#D0D5DD',
                color: '#344054',
                '&:hover': { borderColor: '#98A2B3' },
                minWidth: isMobile ? '70px' : 'auto'
              }}
            >
              Next
            </Button>
          </Stack>
        </Box>
      </Card>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Booking Details</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Customer Name</Typography>
                <Typography variant="body1">{selectedBooking.fullName || selectedBooking.name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{selectedBooking.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{selectedBooking.phone}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Service Type</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {selectedBooking.serviceType || selectedBooking.eventType}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Service Name</Typography>
                <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                  {selectedBooking.serviceName || selectedBooking.serviceCategory}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">Date & Time</Typography>
                <Typography variant="body1">
                  {selectedBooking.consultationDate} at {selectedBooking.consultationTime}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Details</Typography>
                <Typography variant="body1">
                  {selectedBooking.details || selectedBooking.notes || 'No additional details provided.'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Staff Remark</Typography>
                <Typography variant="body1">
                  {selectedBooking.remark || 'No staff remark.'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Booking Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={editedStatus}
              label="Status"
              onChange={(e) => setEditedStatus(e.target.value as Booking['status'])}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="confirmed">Confirmed</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateStatus} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#DC2626' }}>Delete Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this booking?
          </Typography>
          {selectedBooking && (
            <Box sx={{ p: 2, backgroundColor: '#F9FAFB', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {selectedBooking.fullName || selectedBooking.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                {selectedBooking.email}
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085' }}>
                {selectedBooking.serviceType || selectedBooking.eventType} - {formatDate(selectedBooking.consultationDate)}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 2, color: '#DC2626', fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Remark Dialog */}
      <Dialog
        open={remarkDialogOpen}
        onClose={() => !savingRemark && setRemarkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Appointment Remark</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#667085' }}>
              {selectedBooking.fullName || selectedBooking.name} • {formatDate(selectedBooking.consultationDate)} at {selectedBooking.consultationTime}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            label="Internal remark"
            placeholder="Add information for your team..."
            value={remarkText}
            onChange={(event) => setRemarkText(event.target.value)}
            disabled={savingRemark}
            inputProps={{ maxLength: 1000 }}
            helperText={`${remarkText.length}/1000 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemarkDialogOpen(false)} disabled={savingRemark}>
            Cancel
          </Button>
          <Button onClick={handleSaveRemark} variant="contained" disabled={savingRemark}>
            {savingRemark ? 'Saving...' : 'Save remark'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog open={calendarDialogOpen} onClose={() => setCalendarDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #EAECF0',
          pb: 2
        }}>
          <CalendarIcon sx={{ color: '#7C3AED' }} />
          <Box component="span" sx={{ fontWeight: 600, fontSize: '1.25rem' }}>
            Appointment Calendar
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Two Column Layout */}
          <Box sx={{ display: 'flex', minHeight: '600px' }}>
            {/* Left Side - Calendar */}
            <Box sx={{ flex: '0 0 60%', borderRight: '1px solid #EAECF0' }}>
              <CalendarGrid 
                bookingsByDate={getBookingsByDate()}
                onDateClick={(date, dayBookings) => {
                  setSelectedDate(date);
                  setSelectedDateBookings(dayBookings);
                }}
                selectedDate={selectedDate}
                currentMonth={calendarMonth}
                currentYear={calendarYear}
                onMonthChange={handleCalendarMonthChange}
              />
            </Box>
            
            {/* Right Side - Appointment Details */}
            <Box sx={{ flex: '0 0 40%', p: 3, backgroundColor: '#F9FAFB' }}>
              {selectedDate && selectedDateBookings.length > 0 ? (
                <>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#101828' }}>
                    Appointments on {formatDate(selectedDate)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#667085' }}>
                    {getDayOfWeek(selectedDate)} • {selectedDateBookings.length} appointment{selectedDateBookings.length !== 1 ? 's' : ''}
                  </Typography>
                  
                  {/* Appointments List */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2,
                    maxHeight: '400px',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#c1c1c1',
                      borderRadius: '3px',
                    }
                  }}>
                    {selectedDateBookings.map((booking) => (
                      <Box
                        key={booking.id}
                        sx={{
                          p: 2.5,
                          backgroundColor: 'white',
                          borderRadius: '12px',
                          border: '1px solid #E5E7EB',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                          }
                        }}
                      >
                        {/* Customer Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                          <Avatar 
                            sx={{ 
                              width: 40, 
                              height: 40, 
                              fontSize: '0.9rem',
                              backgroundColor: '#7C3AED',
                              color: 'white',
                              fontWeight: 600
                            }}
                          >
                            {getInitials(booking.fullName || booking.name || '')}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#101828', mb: 0.5 }}>
                              {booking.fullName || booking.name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#667085' }}>
                              {booking.email}
                            </Typography>
                          </Box>
                          <Box sx={getStatusBadgeStyle(booking.status)}>
                            {booking.status}
                          </Box>
                        </Box>
                        
                        {/* Service Details */}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', mb: 1 }}>
                            {booking.serviceType || booking.eventType}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#667085', display: 'block', mb: 1 }}>
                            {booking.serviceName || booking.serviceCategory || 'Standard Service'}
                          </Typography>
                          {booking.remark && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                mt: 1,
                                p: 1,
                                color: '#92400E',
                                backgroundColor: '#FFFBEB',
                                borderRadius: '6px',
                                whiteSpace: 'pre-wrap'
                              }}
                            >
                              Remark: {booking.remark}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Time and Contact */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ 
                            color: '#7C3AED', 
                            fontWeight: 600,
                            backgroundColor: '#EFF6FF',
                            px: 2,
                            py: 0.5,
                            borderRadius: '6px',
                            fontSize: '0.75rem'
                          }}>
                            {booking.consultationTime || 'Time TBD'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#667085' }}>
                            {booking.phone}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center'
                }}>
                  <Box sx={{ 
                    width: 80, 
                    height: 80, 
                    borderRadius: '50%', 
                    backgroundColor: '#EFF6FF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2
                  }}>
                    <CalendarIcon sx={{ fontSize: 40, color: '#7C3AED' }} />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#101828', mb: 1 }}>
                    Select a Date
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#667085', maxWidth: 200 }}>
                    Click on a date with appointments to view the appointment details
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCalendarDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Access Pass Generation Dialog */}
      <Dialog open={accessPassDialogOpen} onClose={() => setAccessPassDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#7C3AED', fontWeight: 600 }}>
          Generate Access Pass
        </DialogTitle>
        <DialogContent>
          {accessPassSuccess ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Box sx={{ 
                width: 80, 
                height: 80, 
                borderRadius: '50%', 
                backgroundColor: '#10B981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <VpnKeyIcon sx={{ fontSize: 40, color: 'white' }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#059669', mb: 1 }}>
                Access Pass Created Successfully!
              </Typography>
              <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
                The access pass has been generated and sent.
              </Typography>
              
              {generatedAccessUrl && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ color: '#374151', fontWeight: 500, mb: 2 }}>
                    Access URL Ready:
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: '#F9FAFB', 
                    borderRadius: 2,
                    border: '1px solid #E5E7EB',
                    mb: 2
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#7C3AED', 
                      fontFamily: 'monospace',
                      wordBreak: 'break-all',
                      fontSize: '0.75rem'
                    }}>
                      {generatedAccessUrl}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(generatedAccessUrl);
                          setSnackbar({
                            open: true,
                            message: 'URL copied to clipboard!',
                            severity: 'success'
                          });
                        } catch (err) {
                          console.error('Failed to copy:', err);
                        }
                      }}
                      sx={{ 
                        borderColor: '#7C3AED',
                        color: '#7C3AED',
                        '&:hover': { borderColor: '#6D28D9', backgroundColor: '#F3F4F6' }
                      }}
                    >
                      Copy URL
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => window.open(generatedAccessUrl, '_blank')}
                      sx={{ 
                        backgroundColor: '#7C3AED',
                        '&:hover': { backgroundColor: '#6D28D9' }
                      }}
                    >
                      Open Access Pass
                    </Button>
                  </Stack>
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth error={!!accessPassFormErrors.type}>
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={accessPassForm.type}
                      label="Type"
                      onChange={(e) => handleAccessPassFormChange('type', e.target.value)}
                    >
                      <MenuItem value="Store Visit">Store Visit</MenuItem>
                      <MenuItem value="Office Used">Office Used</MenuItem>
                      <MenuItem value="Delivery">Delivery</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    {accessPassFormErrors.type && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {accessPassFormErrors.type}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Customer Name"
                    value={accessPassForm.customerName}
                    onChange={(e) => handleAccessPassFormChange('customerName', e.target.value)}
                    error={!!accessPassFormErrors.customerName}
                    helperText={accessPassFormErrors.customerName}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    value={accessPassForm.contactNumber}
                    onChange={(e) => handleAccessPassFormChange('contactNumber', e.target.value)}
                    error={!!accessPassFormErrors.contactNumber}
                    helperText={accessPassFormErrors.contactNumber}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Valid Date"
                    type="date"
                    value={accessPassForm.validDate}
                    onChange={(e) => handleAccessPassFormChange('validDate', e.target.value)}
                    error={!!accessPassFormErrors.validDate}
                    helperText={accessPassFormErrors.validDate}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Visit Time In"
                    type="time"
                    value={accessPassForm.pickupTime}
                    onChange={(e) => handleAccessPassFormChange('pickupTime', e.target.value)}
                    error={!!accessPassFormErrors.pickupTime}
                    helperText={accessPassFormErrors.pickupTime}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      step: 300, // 5 min intervals
                      min: "06:00",
                      max: "23:59"
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Visit Time Out"
                    type="time"
                    value={accessPassForm.returnTime}
                    onChange={(e) => handleAccessPassFormChange('returnTime', e.target.value)}
                    error={!!accessPassFormErrors.returnTime}
                    helperText={accessPassFormErrors.returnTime}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      step: 300, // 5 min intervals
                      min: "06:00",
                      max: "23:59"
                    }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ 
                mt: 3, 
                p: 2, 
                backgroundColor: '#F0F9FF', 
                borderRadius: 2,
                border: '1px solid #E0F2FE'
              }}>
                <Typography variant="body2" sx={{ color: '#0369A1', fontWeight: 500 }}>
                  💡 Information
                </Typography>
                <Typography variant="caption" sx={{ color: '#075985', display: 'block', mt: 0.5 }}>
                  This will generate an access pass using the customer details from the booking.
                  The QRID will be set to the default value for ad hoc passes.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          {!accessPassSuccess && (
            <>
              <Button 
                onClick={() => setAccessPassDialogOpen(false)} 
                disabled={accessPassSubmitting}
              >
                Cancel
              </Button>
              <Button 
                onClick={submitAccessPassForm} 
                variant="contained" 
                disabled={accessPassSubmitting}
                sx={{ 
                  backgroundColor: '#7C3AED',
                  '&:hover': { backgroundColor: '#6D28D9' }
                }}
              >
                {accessPassSubmitting ? 'Creating...' : 'Generate Access Pass'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModernBookingDashboard;
